import axios from 'axios';
import qs from 'qs';
import { MessageBox } from 'element-ui';
import {getAuthorizationToken, IS_ADMIN, SHOW_BG} from '@/utils/localstorage-util';
import {
    _includes, _isEmpty, _forOwn, _isObject,
} from '@samclick-lodash';
import i18n from '@/i18next';
import * as appStorageUtil from "@/utils/localstorage-util";
import Cookies from "js-cookie";

function showErrorAlert(error: object) {
    let content = i18n.i18next.t('prompt_unknown_err');

    if (error.response.data && error.response.status !== 500) {
        content = error.response.data.detail;
    } else if (error.response.detail && error.response.status !== 500) {
        content = error.response.detail;
    }
    MessageBox.confirm(i18n.i18next.t(`api_error.${content}`), i18n.i18next.t('api_error.header_error'), {
        confirmButtonText: i18n.i18next.t('buttons.ok'),
        type: 'error',
        center: true,
        showCancelButton: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
    });
}

export function removeAuthentication(): void {
    appStorageUtil.removeItem(appStorageUtil.CURRENT_USER);
    appStorageUtil.removeItem(IS_ADMIN);
    Cookies.remove(appStorageUtil.AUTH_TOKEN);
    Cookies.remove(appStorageUtil.AUTH_TOKEN_LEGACY);
    appStorageUtil.setItem(appStorageUtil.BANNER_IS_CLOSED, false);
    appStorageUtil.removeSessionItem(SHOW_BG);
}

const BASE_API_URL = process.env.VUE_APP_AFF_API_URL;

const axiosInstance = axios.create({
    baseURL: BASE_API_URL,
    withCredentials: false,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'System-Tag': 'MYA',
        Language: i18n.i18next.language,
    },
});

const axiosInstanceMultipart = axios.create({
    baseURL: BASE_API_URL,
    withCredentials: false,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
        'System-Tag': 'MYA',
        Language: i18n.i18next.language,
    },
});

const axiosInstancePublic = axios.create({
    withCredentials: false,
});

axiosInstance.interceptors.request.use(
    (request) => {
        if (request.data) {
            Object.keys(request.data).forEach((key: string) => {
                if (request.data[key] === undefined || request.data[key] === null || request.data[key] === '' || (Array.isArray(request.data[key]) && request.data[key].length === 0)) {
                    delete request.data[key];
                } else if (_isObject(request.data[key])) {
                    const subObjeect = request.data[key];
                    Object.keys(subObjeect).forEach((keysub: string) => {
                        if (subObjeect[keysub] === undefined || subObjeect[keysub] === null || subObjeect[keysub] === '' || (Array.isArray(subObjeect[keysub]) && subObjeect[keysub].length === 0)) {
                            delete subObjeect[keysub];
                        }
                    });
                }
            });
        }

        if (request.params) {
            Object.keys(request.params).forEach((key: string) => {
                if (request.params[key] === undefined || request.params[key] === null || request.params[key] === '' || (Array.isArray(request.params[key]) && request.params[key].length === 0)) {
                    delete request.params[key];
                }
            });
        }
        return request;
    },
);

axiosInstance.interceptors.response.use(
    response => response,
    (error) => {
        if (error.response.status === 401
            && !(_includes(error.response.config.url, 'users/password')
                || _includes(error.response.config.url, 'auth/signin')
                || _includes(error.response.config.url, 'auth/sign-in')
                || _includes(error.response.config.url, 'affiliates/mya/me'))
        ) {
            removeAuthentication()
            window.location.replace('/#/login');
        }

        return Promise.reject(error);
    },
);

export function sendUAPublicGet(url: string, params: object = {}) {
    return axiosInstancePublic.get(url, {
        params,
        paramsSerializer(paramss) {
            return qs.stringify(paramss, { arrayFormat: 'repeat' });
        },
    }).then(response => Promise.resolve(response)).catch(error => Promise.reject(error));
}

export function sendUAGet(url: string, params: object = {}, showError: boolean = false) {
    return axiosInstance.get(url, {
        params,
        paramsSerializer(serializedParams) {
            return qs.stringify(serializedParams, { arrayFormat: 'repeat' });
        },
        headers: {
            Authorization: getAuthorizationToken(),
        },
    }).then(response => Promise.resolve(response)).catch((error) => {
        if (showError) {
            showErrorAlert(error);
        }
        return Promise.reject(error);
    });
}

export function sendUAPost(url: string, data: object = {}, showError: boolean = false) {
    return axiosInstance.post(
        url,
        data,
        {
            headers: {
                Authorization: getAuthorizationToken(),
            },
        },
    ).then(response => Promise.resolve(response)).catch((error) => {
        if (showError) {
            showErrorAlert(error);
        }
        return Promise.reject(error);
    });
}

export function sendUAPut(url: string, data: object = {}, showError: boolean = false) {
    return axiosInstance.put(
        url,
        data,
        {
            headers: {
                Authorization: getAuthorizationToken(),
            },
        },
    ).then(response => Promise.resolve(response)).catch((error) => {
        if (showError) {
            showErrorAlert(error);
        }
        return Promise.reject(error);
    });
}

export function sendUADelete(url: string, showError: boolean = false) {
    return axiosInstance.delete(
        url,
        {
            headers: {
                Authorization: getAuthorizationToken(),
            },
        },
    ).then(response => Promise.resolve(response)).catch((error) => {
        if (showError) {
            showErrorAlert(error);
        }
        return Promise.reject(error);
    });
}

export function sendUAMultipartPost(url: string, data: object = {}, showError: boolean = false) {
    const formData = new FormData();
    if (!_isEmpty(data)) {
        _forOwn(data, (val, key) => {
            formData.append(key, val);
        });
    }
    return axiosInstanceMultipart.post(
        url,
        formData,
        {
            headers: {
                Authorization: getAuthorizationToken(),
            },
        },
    ).then(response => Promise.resolve(response)).catch((error) => {
        if (showError) {
            showErrorAlert(error);
        }
        return Promise.reject(error);
    });
}

export function sendUAGetBlob(url: string, params: object = {}, showError: boolean = false) {
    return axiosInstance.get(url, {
        params,
        paramsSerializer(serializedParams) {
            return qs.stringify(serializedParams, { arrayFormat: 'repeat' });
        },
        headers: {
            Authorization: getAuthorizationToken(),
        },
        responseType: 'blob',
    }).then(response => Promise.resolve(response)).catch((error) => {
        if (showError) {
            showErrorAlert(error);
        }
        return Promise.reject(error);
    });
}

export function sendUAPostBlobResponse(url: string, data: object = {}, showError: boolean = false) {
    return axiosInstance.post(
        url,
        data,
        {
            headers: {
                Authorization: getAuthorizationToken(),
            },
            responseType: 'blob',
        },
    ).then(response => Promise.resolve(response)).catch((error) => {
        if (showError) {
            showErrorAlert(error);
        }
        return Promise.reject(error);
    });
}
